<template>
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        <BaseCard as="section" theme="muted" :padding="isMobile ? 'sm' : 'md'" :aria-label="`${senderName}'s profile preview`">
            <div class="flex items-center gap-2 border-b border-border pb-3 md:gap-3">
                <CollaborationProfileAvatar :size="isMobile ? 'sm' : 'md'" :name="senderName" />
                <div class="min-w-0">
                    <h3 class="nadia-subtitle-small md:nadia-subtitle text-ink">{{ senderName }}</h3>
                    <p class="scaffold-details-small md:scaffold-body-small text-ink-muted">Shared their profile with you</p>
                </div>
            </div>
            <ul class="mt-3 flex flex-col gap-2 md:gap-3">
                <li v-for="trait in isMobile ? traits.slice(0, 4) : traits" :key="trait.text" class="flex items-center gap-2 md:gap-3">
                    <component :is="trait.icon.component" :style="{ color: trait.icon.color }" class="size-4 shrink-0 md:size-5" aria-hidden="true" />
                    <span class="scaffold-details-small text-ink">{{ trait.text }}</span>
                </li>
            </ul>
            <p class="scaffold-details-small text-ink-muted mt-2 md:mt-3">Example profile details</p>
        </BaseCard>
        <BaseCard as="section" :padding="isMobile ? 'sm' : 'md'" :aria-label="`How colleagues see ${viewerName} without a shared profile`">
            <div class="flex items-center gap-2 md:gap-3 md:pb-3">
                <CollaborationProfileAvatar :size="isMobile ? 'sm' : 'md'" :name="viewerName" :available="false" />
                <div class="min-w-0">
                    <h3 class="nadia-subtitle-small md:nadia-subtitle text-ink">{{ viewerName }}</h3>
                    <p class="scaffold-details-small md:scaffold-body-small text-ink-muted">Nadia’s best guess</p>
                </div>
            </div>
            <div class="mt-3 flex flex-col gap-2 md:gap-3" aria-hidden="true">
                <div v-for="width in isMobile ? placeholderWidths.slice(0, 4) : placeholderWidths" :key="width.id" class="flex min-h-3 items-center gap-2 md:min-h-5 md:gap-3">
                    <span class="size-3 shrink-0 rounded-full border-2 border-dashed border-border md:size-4" />
                    <span class="h-1.5 rounded-full bg-border md:h-2" :class="width.className" />
                </div>
            </div>
        </BaseCard>
    </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import BaseCard from "~vue/components/BaseCard.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import { COLLAB_ICONS } from "~vue/utils/collabIcons";

defineProps<{ senderName: string; viewerName: string }>();

const isMobile = useBreakpoints(breakpointsTailwind).smaller("md");

const traits = [
    { icon: COLLAB_ICONS.communicationStyle, text: "Thoughtful about how things land" },
    { icon: COLLAB_ICONS.workStructure, text: "Works best with clear priorities" },
    { icon: COLLAB_ICONS.decisionMaking, text: "Weighs things before deciding" },
    { icon: COLLAB_ICONS.informationProcessing, text: "Keeps the bigger picture in mind" },
    { icon: COLLAB_ICONS.feedbackCollaboration, text: "Values open, constructive input" },
    { icon: COLLAB_ICONS.motivatorsStressors, text: "Motivated by progress and purpose" },
];
const placeholderWidths = [
    { id: 1, className: "w-4/5" },
    { id: 2, className: "w-1/2" },
    { id: 3, className: "w-2/3" },
    { id: 4, className: "w-4/5" },
    { id: 5, className: "w-3/5" },
    { id: 6, className: "w-4/5" },
];
</script>
